/**
 * Clinical Utilities
 * Common utility functions for clinical data operations
 */

import { clinicalDataService } from '../services/clinicalDataService';

// Search and filtering utilities
export const searchUtils = {
  normalizeString: (s) => clinicalDataService.normalizeString(s),
  
  matchesSearch: (item, searchQuery) => clinicalDataService.matchesSearch(item, searchQuery),
  
  filterBySearch: (items, searchQuery) => {
    if (!searchQuery) return items;
    return items.filter(item => clinicalDataService.matchesSearch(item, searchQuery));
  }
};

// Entity filtering utilities
export const entityUtils = {
  filterEntities: (entities, filters) => clinicalDataService.filterEntities({ biological_entities: entities }, filters),
  
  getEntitiesByCategory: (entities, category) => 
    entities.filter(entity => entity.category === category),
  
  getEntitiesByPriority: (entities, priority) => 
    entities.filter(entity => entity.priority === priority),
  
  getEntitiesByStatus: (entities, status) => 
    entities.filter(entity => entity.detectionStatus === status)
};

// Relationship utilities
export const relationshipUtils = {
  getChildren: (structure, type, parentId) => {
    switch (type) {
      case 'buildings':
        return clinicalDataService.getBuildings(structure, parentId);
      case 'floors':
        return clinicalDataService.getFloors(structure, parentId);
      case 'departments':
        return clinicalDataService.getDepartments(structure, parentId);
      case 'rooms':
        return clinicalDataService.getRooms(structure, parentId);
      case 'beds':
        return clinicalDataService.getBeds(structure, parentId);
      case 'equipment':
        return clinicalDataService.getEquipment(structure, parentId);
      default:
        return [];
    }
  },
  
  getParent: (structure, type, itemId) => {
    const item = structure[type]?.find(item => item.id === itemId);
    if (!item) return null;
    
    switch (type) {
      case 'buildings':
        return structure.locations?.find(loc => loc.id === item.locationId);
      case 'floors':
        return structure.buildings?.find(building => building.id === item.buildingId);
      case 'departments':
        return structure.floors?.find(floor => floor.id === item.floorId);
      case 'rooms':
        return structure.departments?.find(dept => dept.id === item.departmentId);
      case 'beds':
        return structure.rooms?.find(room => room.id === item.roomId);
      case 'equipment':
        return structure.departments?.find(dept => dept.id === item.departmentId);
      default:
        return null;
    }
  },
  
  getHierarchy: (structure, type, itemId) => {
    const hierarchy = [];
    let current = structure[type]?.find(item => item.id === itemId);
    
    while (current) {
      hierarchy.unshift(current);
      const parent = relationshipUtils.getParent(structure, type, current.id);
      if (parent) {
        // Find the parent type
        const parentType = Object.keys(structure).find(key => 
          structure[key]?.some(item => item.id === parent.id)
        );
        current = parent;
        type = parentType;
      } else {
        break;
      }
    }
    
    return hierarchy;
  }
};

// Validation utilities
export const validationUtils = {
  validateItem: (type, item) => clinicalDataService.validateItem(type, item),
  
  getValidationErrors: (type, item) => {
    const errors = [];
    const requiredFields = {
      company: ['name', 'address', 'phone', 'email'],
      locations: ['name', 'address', 'contactPerson', 'phone'],
      buildings: ['name', 'address'],
      floors: ['name', 'number'],
      departments: ['name', 'type', 'headDoctor', 'headNurse'],
      rooms: ['name', 'number', 'type', 'capacity'],
      beds: ['number', 'bedType'],
      equipment: ['name', 'type', 'serialNumber', 'manufacturer'],
      personnel: ['name', 'role', 'email', 'phone']
    };

    const fields = requiredFields[type] || [];
    fields.forEach(field => {
      if (!item[field] || item[field].toString().trim() === '') {
        errors.push(`${field} is required`);
      }
    });

    return errors;
  }
};

// Data transformation utilities
export const transformUtils = {
  formatDate: (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  },
  
  formatDateTime: (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  },
  
  formatCurrency: (amount) => {
    if (typeof amount !== 'number') return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  },
  
  formatPercentage: (value, decimals = 1) => {
    if (typeof value !== 'number') return '0%';
    return `${(value * 100).toFixed(decimals)}%`;
  }
};

// Export utilities
export const exportUtils = {
  exportToCSV: (data, filename) => {
    if (!data || data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  },
  
  exportToJSON: (data, filename) => {
    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
};

// Constants
export const CONSTANTS = {
  ENTITY_TYPES: {
    COMPANY: 'company',
    LOCATIONS: 'locations',
    BUILDINGS: 'buildings',
    FLOORS: 'floors',
    DEPARTMENTS: 'departments',
    ROOMS: 'rooms',
    BEDS: 'beds',
    EQUIPMENT: 'equipment',
    PERSONNEL: 'personnel',
    BIOLOGICAL_ENTITIES: 'biological_entities'
  },
  
  STATUS_OPTIONS: {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    MAINTENANCE: 'maintenance',
    CLOSED: 'closed'
  },
  
  PRIORITY_LEVELS: {
    CRITICAL: 'Critical',
    HIGH: 'High',
    MEDIUM: 'Medium',
    LOW: 'Low'
  },
  
  RISK_LEVELS: {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical'
  }
};








